import { useState } from "react";
import SEO from "../components/SEO";
import Reveal from "../components/Reveal";
import AppShotFrame from "../components/AppShotFrame";
import { ChevronLeft, ChevronRight } from "lucide-react";
import FinalCTA from "../sections/FinalCTA";

const STEPS = [
  {
    title: "Dashboard",
    caption:
      "One glance at active clients, today's appointments, open tasks and outstanding invoices — plus a weekly plan and record-integrity checks.",
    shot: "/appstore_ipad_01_dashboard.png",
  },
  {
    title: "Clients",
    caption:
      "Every client's contact info, appointment history, tasks, documents and portal access — filed together and searchable in seconds.",
    shot: "/appstore_ipad_02_clients.png",
  },
  {
    title: "Scheduling & bookings",
    caption:
      "Publish appointment types, take bookings from a shareable link, and let Google Calendar or Outlook keep both sides in sync.",
    shot: "/appstore_ipad_03_bookings.png",
  },
  {
    title: "Tasks",
    caption:
      "Personal, team and client-linked work in one clean list — with priorities, due dates and status, so nothing falls through.",
    shot: "/appstore_ipad_04_tasks.png",
  },
  {
    title: "Forms & documents",
    caption:
      "Intake forms, service requests, contracts, invoices, uploaded documents — all filed under the right client, automatically.",
    shot: "/appstore_ipad_05_forms.png",
  },
  {
    title: "Calendar",
    caption:
      "Every booking, every event, every deadline. Month, week, day and list views — a calendar you actually want to open.",
    shot: "/appstore_ipad_06_calendar.png",
  },
];

export default function ProductTour() {
  const [i, setI] = useState(0);
  const step = STEPS[i];
  const go = (d) => setI((v) => (v + d + STEPS.length) % STEPS.length);
  return (
    <>
      <SEO
        title="Product tour"
        description="A two-minute click-through of the ClientDesk VA workspace — dashboard, clients, bookings, tasks, forms, documents and calendar."
        path="/product-tour"
      />
      <section className="pt-20 pb-8 bg-[#FAF7F2]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#E8712C] mb-3">
              Product tour
            </div>
            <h1 className="font-display font-extrabold text-[36px] sm:text-[52px] leading-[1.05] tracking-[-0.03em] text-[#1A2A3A] mb-5">
              Take a two-minute tour.
            </h1>
            <p className="text-[17px] text-[#57534E] max-w-2xl mx-auto leading-relaxed">
              Real screens from the real app. No sign-up needed.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 bg-[#FAF7F2]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-[1fr_1.15fr] gap-10 items-center">
            <div>
              <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#B85C1F] mb-3">
                Step {i + 1} of {STEPS.length}
              </div>
              <h2 className="font-display font-extrabold text-[28px] sm:text-[36px] leading-[1.1] tracking-[-0.02em] text-[#1A2A3A] mb-4">
                {step.title}
              </h2>
              <p className="text-[16px] text-[#57534E] leading-relaxed mb-8">
                {step.caption}
              </p>
              <div className="flex items-center gap-2">
                <button
                  data-testid="tour-prev"
                  onClick={() => go(-1)}
                  className="w-11 h-11 rounded-full border border-[#E8E2D9] bg-white text-[#1A2A3A] flex items-center justify-center hover:border-[#E8712C]/40"
                  aria-label="Previous step"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  data-testid="tour-next"
                  onClick={() => go(1)}
                  className="w-11 h-11 rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white flex items-center justify-center"
                  aria-label="Next step"
                >
                  <ChevronRight size={18} />
                </button>
                <div className="ml-3 flex gap-1.5">
                  {STEPS.map((_, idx) => (
                    <button
                      key={idx}
                      aria-label={`Go to step ${idx + 1}`}
                      onClick={() => setI(idx)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === i ? "bg-[#E8712C]" : "bg-[#D6C8B4]"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            <AppShotFrame
              key={step.shot}
              src={step.shot}
              alt={`${step.title} in ClientDesk VA`}
              variant="tablet"
            />
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
